import { isTableRunHostname } from 'common/tablerun/hosts';
import { useEffect } from 'react';
import { useLocation } from 'src/shared/hooks/useLocation';
import { isProdEnvironment } from 'src/shared/utils/EnvironmentUtilities';

const TABLERUN_OVERRIDE_STORAGE_KEY = 'tablerun-host-override';

const readHostname = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.hostname;
};

const readOverride = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.sessionStorage.getItem(TABLERUN_OVERRIDE_STORAGE_KEY) === '1';
};

/**
 * Single source of truth for whether the current request should render in
 * TableRun (whitelabel) mode versus marketplace Shef mode. Purely hostname-
 * based in production; `?as=tablerun` sets a sticky sessionStorage override on
 * shef.com hosts for dev testing, `?as=shef` clears it.
 *
 * The sessionStorage sync happens in an effect so render stays pure; the render
 * return value already reflects the current `?as=` param without needing the
 * write to land first.
 */
export const useIsTableRunHost = (): boolean => {
  const location = useLocation();
  const asParam = new URLSearchParams(location.search).get('as');

  useEffect(() => {
    if (typeof window === 'undefined' || isProdEnvironment()) {
      return;
    }
    if (asParam === 'shef') {
      window.sessionStorage.removeItem(TABLERUN_OVERRIDE_STORAGE_KEY);
    } else if (asParam === 'tablerun') {
      window.sessionStorage.setItem(TABLERUN_OVERRIDE_STORAGE_KEY, '1');
    }
  }, [asParam]);

  if (typeof window === 'undefined') {
    return false;
  }
  if (isTableRunHostname(readHostname())) {
    return true;
  }
  if (isProdEnvironment()) {
    return false;
  }
  if (asParam === 'shef') {
    return false;
  }
  if (asParam === 'tablerun') {
    return true;
  }
  return readOverride();
};
